import Loader from 'reusable/mobx-helpers/Loader'
import { makeAutoObservable } from 'mobx'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import ProposalsEditStore from '../ProposalsEditStore'
import { notNull } from '@gap/utils/utils'
import { ServiceDto } from '@gap/backend/app-services/services/models/ServiceDto'
import { DropResult } from '@hello-pangea/dnd'
import { ProposalStep2Dto, ProposalStep2ServiceDto } from '@gap/backend/app-services/proposal/models/ProposalStep2Dto'
import variables from 'variables/variables'

interface ProposalsEditServicesStoreProps {
   proposalsEditStore: ProposalsEditStore
}

interface FormFields {
   selectedServices: ServiceDto[]
}

export default class ProposalsEditServicesStore {
   loader = new Loader(false)
   fields: FormFields = {
      selectedServices: [],
   }
   isDragging = false
   isInitialised = false
   sp

   constructor(sp: ProposalsEditServicesStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get formHandler() {
      return formHandler(this.fields, {
         selectedServices: { type: 'any' },
      } satisfies FormHandlerConfig)
   }

   get selectedServices() {
      return this.sp.proposalsEditStore.proposal.step2 || []
   }

   get serviceStore() {
      return this.sp.proposalsEditStore.sp.serviceStore
   }

   get step(): ProposalStep2Dto {
      if (!this.sp.proposalsEditStore.proposal.step2) {
         this.sp.proposalsEditStore.proposal.step2 = []
      }
      return this.sp.proposalsEditStore.proposal.step2
   }

   set step(step: ProposalStep2Dto) {
      this.sp.proposalsEditStore.proposal.step2 = step
      this.sp.proposalsEditStore.autoSave.updateProposal()
   }

   initialiseSelectedServices = () => {
      this.step = this.step.filter(stepService => {
         const service = this.sp.proposalsEditStore.sp.serviceStore.services.find(service => service.id === stepService.serviceId)
         if (service) {
            this.fields.selectedServices.push(service)
         } else {
            this.deleteServiceById(stepService.serviceId)
         }
         return !!service
      })
      this.markStepStatus()
      this.isInitialised = true
   }

   markStepStatus = () => {
      this.sp.proposalsEditStore.stepperFullscreenStore.markStepStatus(this.fields.selectedServices.length >= 1)
   }

   onNewServiceAdded = (serviceData: string | ServiceDto[]) => {
      if (typeof serviceData === 'string') {
         this.sp.proposalsEditStore.sp.serviceStore.addServiceWithStartingValue(serviceData)
      } else {
         const lastService = serviceData[serviceData.length - 1]
         if (lastService?.orgId === variables.gapOrgId) {
            this.serviceStore.openServiceDrawer('add')
            this.serviceStore.fillFieldsForService(lastService, { init: true })
            this.fields.selectedServices = this.fields.selectedServices.filter(x => x.id !== lastService.id)
            return
         }
         this.syncServices(serviceData)
         this.fields.selectedServices = serviceData
      }

      this.markStepStatus()
   }

   /**
    * We need to sync the services stored in the form(aka the ones we have selected) and the ones shown on screen
    */
   syncServices = (serviceData: ServiceDto[]) => {
      const action = this.fields.selectedServices.length >= serviceData.length ? 'DELETE' : 'ADD'

      if (action === 'ADD') {
         this.addService(notNull(serviceData[serviceData.length - 1]).id)
      } else {
         this.deleteService(serviceData)
      }
      this.markStepStatus()
   }

   deleteServiceById = (id: string) => {
      this.step = this.step.filter(({ serviceId }) => id !== serviceId)
      this.fields.selectedServices = this.fields.selectedServices.filter(x => x.id !== id)
      this.sp.proposalsEditStore.deleteServiceFromStep2(id, this.step.length === 0)
      this.markStepStatus()
   }

   deleteService = (serviceData: ServiceDto[]) => {
      let deletedServiceId: string | undefined
      const filteredStep = this.step.filter(stepService => {
         const found = serviceData.find(({ id }) => id === stepService.serviceId)
         if (!found) {
            deletedServiceId = stepService.serviceId
         }
         return found
      })
      this.sp.proposalsEditStore.deleteServiceFromStep2(notNull(deletedServiceId), serviceData.length === 0)
      if (!filteredStep.length) {
         this.sp.proposalsEditStore.proposal.editingStepIndex = this.sp.proposalsEditStore.stepperFullscreenStore.activeStepIndex
      }
      this.step = filteredStep
   }

   addService = (serviceId: string) => {
      const service = notNull(this.serviceStore.services.find(service => service.id === serviceId))

      this.fields.selectedServices.push(service)

      const existingService = this.step.find(step => step.serviceId === serviceId)

      const newService: ProposalStep2ServiceDto = {
         serviceDefinition: service.description || '',
         serviceHowItWorks: service.howItWorks || '',
         serviceId: service.id,
         serviceName: service.name,
         servicePriceType: 'agreed'
      }

      if (existingService) {
         // Remove service from step(2)
         this.step = this.step.filter(stepService => serviceId !== stepService.serviceId)

         // re-add service to fields, so that we have no duplicates and the correct (new) name
         this.fields.selectedServices = this.fields.selectedServices.filter(x => x.id !== serviceId)
         this.fields.selectedServices.push(service)
      } else {
         // It is a new service
         this.sp.proposalsEditStore.addServiceFromStep2(serviceId)
      }

      // Add a timeout, otherwise it is seen as 1 action and the service wont update on screen
      setTimeout(() => {
         this.step = [...this.step, newService]
      }, 10)

      this.markStepStatus()
   }

   onDragEnd = async (result: DropResult) => {
      const { destination, source } = result

      // return if there's no destination or the destination hasn't updated
      if (!destination || (destination.index === source.index)) {
         return
      }

      const reOrderedList: ProposalStep2ServiceDto[] = []

      this.step.forEach((stepService, index) => {
         switch (index) {
            case destination.index:
               if (destination.index > source.index) {
                  reOrderedList.push(stepService)
                  reOrderedList.push(this.step[source.index])
               } else {
                  reOrderedList.push(this.step[source.index])
                  reOrderedList.push(stepService)
               }
               break
            case source.index:
               break
            default:
               reOrderedList.push(stepService)
         }
      })

      this.step = reOrderedList
   }

   onStopReordering = () => {
      this.isDragging = false
   }
}

export const { Provider: ProposalsEditServicesStoreProvider, useProvidedStore: useProposalsEditServicesStore } = createStoreContext(ProposalsEditServicesStore)
